import React from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Toaster } from 'react-hot-toast'
import * as Sentry from '@sentry/react'
import { LocalizationProvider } from './contexts/LocalizationContext'
import { ConfirmProvider } from './components/ui'
import App from './App.tsx'
import './index.css'

// Sentry — initialized only when DSN is provided. Errors-only profile to
// stay within the free tier on a low-traffic admin tool (no tracing, no
// session replays). Environment tag lets us filter dev noise in the UI.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.PROD ? 'production' : 'development',
    tracesSampleRate: 0,
    // sendDefaultPii: false — Sentry's default. Don't include user IPs/cookies.
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <LocalizationProvider>
        <BrowserRouter>
          <ConfirmProvider>
            <App />
          </ConfirmProvider>
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: 'rgb(var(--bg-elevated))',
                color: 'rgb(var(--text-primary))',
                border: '1px solid rgb(var(--border-strong))',
                borderRadius: '14px',
                padding: '12px 16px',
                fontFamily: 'Inter, -apple-system, sans-serif',
                fontSize: '14px',
                boxShadow: '0 8px 24px -4px rgb(0 0 0 / 0.18)',
              },
              success: {
                iconTheme: {
                  primary: 'rgb(var(--success))',
                  secondary: 'rgb(var(--bg-elevated))',
                },
              },
              error: {
                iconTheme: {
                  primary: 'rgb(var(--error))',
                  secondary: 'rgb(var(--bg-elevated))',
                },
              },
            }}
          />
        </BrowserRouter>
      </LocalizationProvider>
    </QueryClientProvider>
  </React.StrictMode>,
)
